// src/components/VaccineProtectRequisitionList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Input,
  Heading,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// ฟังก์ชันกำหนดสไตล์สถานะ
const getStatusStyle = (status) => {
  switch (status) {
    case "Pending":
      return { bg: "#FFF3E0", color: "#D97706" };
    case "Confirmed":
      return { bg: "#D1FAE5", color: "#047857" };
    case "Completed":
      return { bg: "#E5E7EB", color: "#374151" };
    case "Cancelled":
      return { bg: "#FEE2E2", color: "#991B1B" };
    default:
      return { bg: "#EDF2F7", color: "#4A5568" };
  }
};

// ฟังก์ชันกำหนดสไตล์สถานะการอนุมัติ
const getApprovalStatusStyle = (status) => {
  switch (status) {
    case "Pending":
      return { bg: "#FFF3E0", color: "#D97706" };
    case "Approved":
      return { bg: "#D1FAE5", color: "#047857" };
    case "Rejected":
      return { bg: "#FEE2E2", color: "#991B1B" };
    default:
      return { bg: "#EDF2F7", color: "#4A5568" };
  }
};

const VaccineProtectRequisitionList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [requisitions, setRequisitions] = useState([]);
  const [filteredRequisitions, setFilteredRequisitions] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchYear, setSearchYear] = useState("");
  const [searchUnit, setSearchUnit] = useState("");

  const unitOptions = [
    "กลุ่มงานโคเนื้อ",
    "กลุ่มงานโคนม",
    "กลุ่มงานสัตว์ปีก",
    "กลุ่มงานสุกร",
  ];

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.TransactionID,
      sortable: true,
    },
    {
      name: "วันที่ขอเบิก",
      selector: (row) => new Date(row.RequestDate).toLocaleString(),
      sortable: true,
    },
    {
      name: "ปีงบประมาณ",
      selector: (row) => row.EstimateYear,
      sortable: true,
    },
    {
      name: "กลุ่มงาน",
      selector: (row) => row.EstimateUnit,
      sortable: true,
    },
    {
      name: "ส่งมอบไปที่",
      selector: (row) => row.DeliveryToName,
      sortable: true,
    },
    {
      name: "ประเภทงบประมาณ",
      selector: (row) => row.BudgetType,
      sortable: true,
    },
    /*
    {
      name: "สถานะการอนุมัติ",
      selector: (row) => (
        <Box
          bg={getApprovalStatusStyle(row.ApprovalStatus).bg}
          color={getApprovalStatusStyle(row.ApprovalStatus).color}
          borderColor={getApprovalStatusStyle(row.ApprovalStatus).color}
          borderWidth={1}
          borderRadius="full"
          px={3}
          py={1}
          textAlign="center"
          fontSize="sm"
          fontWeight="semibold"
          display="inline-block"
        >
          {row.ApprovalStatus}
        </Box>
      ),
      sortable: true,
      width: "150px",
    },
    */
    {
      name: "สถานะ",
      selector: (row) => (
        <Box
          bg={getStatusStyle(row.Status).bg}
          color={getStatusStyle(row.Status).color}
          borderColor={getStatusStyle(row.Status).color}
          borderWidth={1}
          borderRadius="full"
          px={3}
          py={1}
          textAlign="center"
          fontSize="sm"
          fontWeight="semibold"
          display="inline-block"
        >
          {row.Status}
        </Box>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccineprotectrequisition/${row.TransactionID}`)}
          label="ดูรายละเอียด"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchRequisitions = async () => {
    setIsLoading(true);
    try {
      await fetchWarehouses();
      const response = await apiService({
        method: "POST",
        url: "/getVaccineProtectRequisitionsList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineProtectRequisitionsList || [];
        setRequisitions(data);
        setFilteredRequisitions(data);
      }
    } catch (error) {
      console.error("Error fetching requisitions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchRequisitions();
    }
  }, [sessionId]);

  const handleSearch = () => {
    if (!searchYear && !searchUnit) {
      setFilteredRequisitions(requisitions);
      return;
    }

    const filtered = requisitions.filter((row) => {
      const yearMatch = searchYear
        ? row.EstimateYear.toLowerCase().includes(searchYear.toLowerCase())
        : true;
      const unitMatch = searchUnit
        ? row.EstimateUnit === searchUnit
        : true;
      return yearMatch && unitMatch;
    });
    setFilteredRequisitions(filtered);
  };

  const handleYearChange = (e) => {
    setSearchYear(e.target.value);
  };

  const handleUnitChange = (e) => {
    setSearchUnit(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchYear("");
    setSearchUnit("");
    setFilteredRequisitions(requisitions);
  };

  const handleCreateNew = () => {
    navigate("/vaccineprotectrequisition/new");
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการใบเบิกวัคซีนงบป้องกัน
            </Heading>
          </HStack>
        </Box>
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          {requisitions.length > 0 && (
            <HStack
              w={{ base: "100%", md: "70%" }}
              spacing={4}
              flexWrap={{ base: "wrap", md: "nowrap" }}
            >
              <InputGroup w={{ base: "100%", md: "50%" }}>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faSearch} color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="ค้นหาปีงบประมาณ..."
                  value={searchYear}
                  onChange={handleYearChange}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  colorScheme="iBlue"
                  mt={1}
                />
              </InputGroup>
              <Select
                w={{ base: "100%", md: "50%" }}
                placeholder="เลือกกลุ่มงาน"
                value={searchUnit}
                onChange={handleUnitChange}
                onBlur={handleSearch}
                colorScheme="iBlue"
                mt={{ base: 4, md: 1 }}
              >
                {unitOptions.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Select>
              <IconButton.Clear onClick={handleClearSearch} mt={{ base: 4, md: 0 }} />
            </HStack>
          )}
          <IconButton.Add onClick={handleCreateNew} label="เพิ่มรายการใหม่" mt={{ base: 4, md: 0 }} />
        </HStack>
        {isLoading ? (
          <Spinner size="md" />
        ) : requisitions.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลใบเบิกวัคซีนงบป้องกัน
          </Text>
        ) : filteredRequisitions.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredRequisitions}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineProtectRequisitionList;